import React, { useEffect, useRef, useCallback } from 'react';

interface ClickAwayListenerProps {
  /**
   * The content to be rendered inside the click away listener
   */
  children: React.ReactNode;

  /**
   * Callback function to be called when a click outside is detected
   */
  onClickAway: (event: MouseEvent | TouchEvent) => void;

  /**
   * Whether the listener is active
   * @default true
   */
  active?: boolean;

  /**
   * The mouse events to listen to
   * @default 'click'
   */
  mouseEvent?: 'click' | 'mousedown' | 'mouseup' | 'touchend' | false;

  /**
   * Whether to use passive event listeners for better performance
   * @default true
   */
  passive?: boolean;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

/**
 * ClickAwayListener component that detects clicks outside of its children
 * This is an optimized implementation that works well on both desktop and mobile devices
 */
const ClickAwayListener: React.FC<ClickAwayListenerProps> = ({
  children,
  onClickAway,
  active = true,
  mouseEvent = 'click',
  passive = true,
  className,
  style
}) => {
  const rootRef = useRef<HTMLDivElement>(null);

  // Memoize the handler to prevent unnecessary re-renders
  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      // Do nothing if clicking ref's element or descendent elements
      if (!rootRef.current) {
        return;
      }

      // Get the document from the ref or fall back to the global document
      const doc = (rootRef.current.ownerDocument || document) as Document;

      // Get the target element
      const targetElement = event.target as Node;

      // Check if the click target is part of a MUI Select dropdown or Menu
      const isPartOfSelectDropdown = (target: Node): boolean => {
        // First check if any select is open in the document
        const openSelects = document.querySelectorAll('[aria-expanded="true"]');
        if (openSelects.length > 0) {
          // If any select is open, we should prevent click away
          return true;
        }

        // Check if the target or any of its parents have MUI Select or Menu related classes
        let current: Node | null = target;
        while (current) {
          if (current instanceof Element) {
            // Check for MUI Select dropdown elements
            if (
              // Check for MUI Popover/Menu classes
              current.classList.contains('MuiPopover-root') ||
              current.classList.contains('MuiMenu-root') ||
              current.classList.contains('MuiMenuItem-root') ||
              current.classList.contains('MuiSelect-select') ||
              current.classList.contains('MuiSelect-root') ||
              current.classList.contains('MuiBackdrop-root') ||
              current.classList.contains('MuiPopover-paper') ||
              // Check for MUI Select roles
              current.getAttribute('role') === 'listbox' ||
              current.getAttribute('role') === 'option' ||
              current.getAttribute('role') === 'presentation' ||
              // Check for our custom data attributes
              current.getAttribute('data-testid') === 'select-dropdown' ||
              current.getAttribute('data-disable-backdrop-click') === 'true' ||
              // Check for aria attributes that indicate dropdown
              current.getAttribute('aria-expanded') === 'true' ||
              current.getAttribute('aria-haspopup') === 'listbox'
            ) {
              return true;
            }
          }
          current = current.parentNode;
        }
        return false;
      };

      // Check if the click was outside the component
      if (
        // Ensure the target is within the document
        doc.documentElement.contains(targetElement) &&
        // Ensure the target is not within our component
        !rootRef.current.contains(targetElement) &&
        // Ensure the target is not part of a dropdown menu
        !isPartOfSelectDropdown(targetElement)
      ) {
        // Call the callback with the event
        onClickAway(event);
      }
    },
    [onClickAway]
  );

  useEffect(() => {
    // If not active or no mouse event specified, don't set up listeners
    if (!active || !mouseEvent) {
      return undefined;
    }

    // Options for event listeners
    // Set passive to false to allow preventDefault() to work
    const options = { passive: false, capture: true };

    // Add event listeners with options
    document.addEventListener(mouseEvent, handleClickAway, options);

    // Always add touchend for mobile devices if not already added
    if (mouseEvent !== 'touchend') {
      document.addEventListener('touchend', handleClickAway, options);
    }

    // Clean up event listeners
    return () => {
      document.removeEventListener(mouseEvent, handleClickAway);
      if (mouseEvent !== 'touchend') {
        document.removeEventListener('touchend', handleClickAway);
      }
    };
  }, [active, mouseEvent, handleClickAway, passive]);

  return (
    <div
      ref={rootRef}
      className={className}
      style={style}
    >
      {children}
    </div>
  );
};

export default ClickAwayListener;
